import React from 'react';
import { InlineLoader } from './InlineLoader';

interface PdfThumbProps {
  src: string;
  className?: string;
  frame?: 'square' | 'portrait4x5';
}

/**
 * Renders the first page of a remote/local PDF into an <img> (data URL).
 * Lightweight thumbnail generator used in lists/tooltips.
 */
export const PdfThumb: React.FC<PdfThumbProps> = ({ src, className = '', frame = 'square' }) => {
  const [thumbUrl, setThumbUrl] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);

  const loadPdfJs = React.useCallback(async () => {
    const w = window as any;
    if (w.pdfjsLib) return w.pdfjsLib;
    await new Promise<void>((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js';
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Failed to load pdf.js'));
      document.head.appendChild(s);
    });
    const lib = (window as any).pdfjsLib;
    if (lib) lib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
    return lib;
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    async function generate() {
      try {
        setIsLoading(true);
        setError(null);
        const pdfjsLib = await loadPdfJs();
        const effectiveUrl = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
          ? `/api/pdf-proxy?url=${encodeURIComponent(src)}`
          : src;
        const resp = await fetch(effectiveUrl);
        const buf = await resp.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
        const page = await pdf.getPage(1);
        const baseViewport = page.getViewport({ scale: 1 });
        let canvasWidth = 256;
        let canvasHeight = 256;
        if (frame === 'portrait4x5') {
          canvasWidth = 300; // small but sharp enough
          canvasHeight = 375; // 4:5
        }
        const scale = Math.min(canvasWidth / baseViewport.width, canvasHeight / baseViewport.height);
        const vp = page.getViewport({ scale });

        const pageCanvas = document.createElement('canvas');
        const pageCtx = pageCanvas.getContext('2d');
        if (!pageCtx) throw new Error('No canvas context');
        pageCanvas.width = Math.floor(vp.width);
        pageCanvas.height = Math.floor(vp.height);
        await page.render({ canvasContext: pageCtx, viewport: vp }).promise;

        const frameCanvas = document.createElement('canvas');
        const fctx = frameCanvas.getContext('2d');
        if (!fctx) throw new Error('No canvas context');
        frameCanvas.width = canvasWidth;
        frameCanvas.height = canvasHeight;
        fctx.fillStyle = '#ffffff';
        fctx.fillRect(0, 0, canvasWidth, canvasHeight);
        const offX = Math.floor((canvasWidth - pageCanvas.width) / 2);
        const offY = Math.floor((canvasHeight - pageCanvas.height) / 2);
        fctx.drawImage(pageCanvas, offX, offY);
        const dataUrl = frameCanvas.toDataURL('image/png');
        if (!cancelled) setThumbUrl(dataUrl);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'PDF render failed');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    if (/\.pdf(\?|#|$)/i.test(src)) generate();
    else { setThumbUrl(null); setIsLoading(false); }
    return () => { cancelled = true; };
  }, [src, frame, loadPdfJs]);

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <InlineLoader size="sm" />
      </div>
    );
  }

  if (error || !thumbUrl) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 text-gray-500 text-[10px] ${className}`}>PDF</div>
    );
  }

  return (
    <img src={thumbUrl} alt="PDF page 1" className={`w-full h-full object-contain ${className}`} />
  );
};


